<template>
	<div>
			<v-container fluid class="pt-3 mb-1">
					<v-layout row wrap>
						<v-flex xl3 lg4 md6 sm6 xs12 class="pr-3">	
							<v-text-field v-debounce:300ms="updateSearch" ref="focusAfterTagSelector" append-icon="search" label="Search" autofocus="" background-color="#fff"></v-text-field>
						</v-flex>
						<v-flex xl3 lg4 md6 sm6 xs12 class="pr-3">
							<tag-selector :options="tagList"
                                        :selectedOptions="selectedTags"
                                        :isMatchAll="matchAllTags"
                                        placeholder="Enter Tag"
                                        @on-selected-tags-change="updateSelectedTags"
                                        @on-match-all-tags-change="updateMatchAllTags"
                                        @on-leave="onLeaveTagSelector"></tag-selector>
						</v-flex>
						<v-flex></v-flex>
						<v-flex shrink class="mt-4">
							<v-switch v-model="showInactive" style="margin:0; padding: 0;">
								<template v-slot:label>
									<span>Show Inactive Employees</span>
								</template>
							</v-switch>
						</v-flex>
					</v-layout>

					<v-data-table :headers="headers" :items="employeesFiltered" :loading="employees_loading" :pagination.sync="pagination" :rows-per-page-items="rowsPerPageItems" :search="search" class="elevation-1 mt-2">
						<v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>

						<template slot="items" slot-scope="props">
							<tr :class="{ inactive: !props.item.isActive }">
								<td class="text-xs-left">
									<router-link :to="{ name: 'employee', params: { id: props.item.id } }">
										<v-avatar style="margin-top:5px; margin-bottom:5px;" :size="60" color="grey lighten-4">
											<img :src="getAvatar(props.item.id)" alt="avatar" class="avatar-img" />
										</v-avatar>
									</router-link>
								</td>
								<td class="text-xs-left">
									<router-link :to="{ name: 'employee', params: { id: props.item.id } }">{{ props.item.name }}</router-link>
								</td>
								<td class="text-xs-left">{{ props.item.department }}</td>
								<td class="text-xs-left" v-if="hasPositionDescriptionsModule">
									<v-layout>
										<v-flex v-if="showPositionStatusDot(props.item)" shrink class="pr-1">
											<v-icon small :color="getPositionStatusDotColor(props.item)">mdi-circle</v-icon>
										</v-flex>
										<v-flex>
											<span v-if="props.item.hasLivePosition">
												<router-link :to="{ name: 'employeePosition', params: { employeeId: props.item.id, id: getLatestLivePosition(props.item).id }}">
													{{ getPositionTitle(props.item) }}
												</router-link>
												<div v-if="hasNewerPds(props.item)" class="info-text">
													<v-icon small color="warning" class="mr-1">mdi-pencil-outline</v-icon>
													<router-link :to="{ name: 'employeePosition', params: { employeeId: props.item.id, id: getLatestNonLivePosition(props.item).id }}">PD has pending changes					
													</router-link>
													<span>
														- 
														<span v-if="getLatestNonLivePosition(props.item).isApproved">Awaiting employee acknowledgement</span>
														<span v-else>Not yet approved</span>
													</span>
												</div>
											</span>
											
											<span v-else-if="getLatestNonLivePosition(props.item)">
												<router-link :to="{ name: 'employeePosition', params: { employeeId: props.item.id, id: getLatestNonLivePosition(props.item).id }}">
													{{ getPositionTitle(props.item) }}
												</router-link>
												<div class="info-text">
													<span v-if="getLatestNonLivePosition(props.item).isApproved">PD awaiting employee acknowledgement</span>
													<span v-else>PD not yet approved</span>
												</div>						
											</span>
											<span v-else class="info-text">
												No PD setup
											</span>
										</v-flex>
									</v-layout>
									
								</td>
								<td class="text-xs-left">
									<tag-items :tags="props.item.tags" read-only />
								</td>
								<td class="text-xs-left">
									<v-icon v-if="props.item.hasLogin == true" color="success" small class="mr-2">
										mdi-check-circle-outline
									</v-icon>
									<v-icon v-if="props.item.hasLogin == false" color="error" small class="mr-2">
										mdi-close-circle-outline
									</v-icon>
								</td>
								<!-- <td class="justify-center px-0">
									<v-icon small class="mr-2" @click="editEmployee(props.item)">
										edit
									</v-icon>
								</td> -->
							</tr>
						</template>
						<template v-slot:no-results>
							<v-alert :value="true" color="info" outline icon="warning"> Your search for "{{ search }}" found no results. </v-alert>
						</template>
					</v-data-table>
			</v-container>

		<v-footer color="" app height="55">
			<v-spacer></v-spacer>
			<v-btn v-if="loggedInEmployee != null && loggedInEmployee.isAdmin" @click="onAddEmployeeClick" color="primary"> <v-icon class="mr-2">add</v-icon> Add New Employee </v-btn>
		</v-footer>
	</div>
</template>

<script>
	import { mapState, mapGetters } from 'vuex';
	import avatarMixin from '@/mixins/avatarMixin';
	import TagSelector from '@/components/tags/TagSelector';
	import TagItems from '@/components/tags/TagItems';
	import positionStatusMixin from '@/mixins/positionStatusMixin';

	export default {
		components: {
			TagSelector,
			TagItems
		},
		mixins: [avatarMixin, positionStatusMixin],
		created() {
			this.$store.dispatch('setPageTitle', 'Employees');
			if (this.hasPositionDescriptionsModule) {
				this.headers.splice(3, 0, { text: 'Position Title', value: 'positionTitle' });
			}
		},
		data() {
			return {
				headers: [
					{
						text: 'Photo',
						align: 'left',
						sortable: false,
						value: 'photoUrl'
					},
					{ text: 'Employee', value: 'name' },
					{ text: 'Department', value: 'department' },
					{ text: 'Tags', value: 'tags' },
					{ text: 'Has Access', value: 'hasLogin' }
				],
				showInactive: false,
				pagination: {
					sortBy: 'name',
					rowsPerPage: 30
				},
				rowsPerPageItems: [8, 15, 30, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }],
				search: '',
				selectedTags: [],
				matchAllTags: false
			};
		},
		computed: {
			...mapGetters({
				employees: 'employees/employeeList',
				loggedInEmployee: 'loggedInEmployee',
				loggedInTenant: 'loggedInTenant',
				tagList: 'tags/employeeTagList'
			}),
			...mapState({
				employees_loading: state => state.employees.employee_loading
			}),
			employeesFiltered() {
				const filterOptions = {
					search: this.search,
					tags: this.selectedTags,
					matchAllTags: this.matchAllTags,
					includeInactive: this.showInactive
				};
				const filteredEmployeeIds = this.$_getFilteredEmployeeIds(this.loggedInEmployee, filterOptions);
				return this.employees.filter(x => filteredEmployeeIds.includes(x.id));
			},
			hasPositionDescriptionsModule() {
				return this.$_hasAccessToModule('positionDescriptions');
			}
		},
		methods: {
			onAddEmployeeClick() {
				if (this.loggedInEmployee.isAdmin) {
					this.$router.push('/employee/create/');
				}			
			},
			editEmployee(item) {
				this.$router.push(`/employee/${item.id}/edit/`);
			},
			deleteEmployee(item) {
				confirm('Are you sure you want to delete this Employee?') && this.$store.dispatch('employees/deleteEmployee', item);
			},
			updateSelectedTags(newSelectedTags) {
				this.selectedTags = newSelectedTags;
			},
			updateMatchAllTags(newMatchAll) {
				this.matchAllTags = newMatchAll;
			},
			onLeaveTagSelector() {
				this.$refs.focusAfterTagSelector.focus();
			},
			updateSearch(val) {
				this.search = val;
			},
			getPositionTitle(employee) {
				if (employee.hasLivePosition) return employee.positionTitle; // Latest live

				return employee.positions.length > 0 ? employee.positions[0].title : "N/A";
			},
			getLatestNonLivePosition(employee) {
				var nonLive = employee.positions.filter(x => !this.isLive(x));
				if (nonLive.length > 0) {
					return nonLive[0];
				}

				return null;
			},
			getLatestLivePosition(employee) {
				return employee.positions.filter(x => this.isLive(x))[0];
			},
			getPositionStatusDotColor(employee) {
				const anyLive = employee.positions.some(x => this.isLive(x));
				return anyLive ? "success" : "warning";
			},
			showPositionStatusDot(employee) {
				return employee.positions.length > 0;
			},
			hasNewerPds(employee) {
				if (!employee.hasLivePosition) return false;

				const latestLive = employee.positions.filter(x => this.isLive(x))[0];

				return employee.positions.some(x => !this.isLive(x) && new Date(x.startDateUTC) >= new Date(latestLive.startDateUTC));
			}
		}
	};
</script>

<style scoped>
	tr.inactive {
		background-color: #ffebee;
	}
	tr.inactive:hover {
		background-color: #ffcdd2 !important;
	}
	.info-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px !important;
		font-style: italic;
	}
</style>
